import { useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post } from "@common/api/apiShared";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { getRiskAssessmentSubjectUrl } from "../RiskAssessmentsOverview.utils";
import { RiskScenarioCategory } from "../../../riskSettings/riskScenarios/riskScenarioDialog/RiskScenarioDialog.types";
import { IAddRiskScenarioDialog } from "./AddRiskScenarioDialog";
import { SelectableRiskScenario } from "./AddRiskScenarioDialog.types";

export interface SelectableScenarioRow {
    id: string;
    name: string;
    description?: string;
    category: RiskScenarioCategory;
    categoryLabel: string;
    isSelected: boolean;
}

// Mirrors the internal search of DotLegalTableWithControls (case-insensitive substring across the
// non-excluded columns name + categoryLabel, with "exact" quote handling) so select-all and the
// header checkbox act only on rows the table is actually showing.
export function scenarioMatchesSearch(row: SelectableScenarioRow, searchString: string): boolean {
    if (!searchString) return true;

    const firstQuoteIndex = searchString.indexOf('"');
    const lastQuoteIndex = searchString.lastIndexOf('"');

    let exactString = "";
    if (firstQuoteIndex > -1 && lastQuoteIndex > -1 && firstQuoteIndex !== lastQuoteIndex) {
        exactString = searchString.substring(firstQuoteIndex + 1, lastQuoteIndex);
    }

    const needle = firstQuoteIndex === lastQuoteIndex ? searchString.slice(firstQuoteIndex + 1) : searchString;

    const searchableValues = [row.name, row.categoryLabel].map((value) => value.toLowerCase());

    if (exactString.trim().length === 0) {
        return searchableValues.some((value) => value.includes(needle.toLowerCase()));
    }
    return searchableValues.some((value) => value === exactString.toLowerCase());
}

export function useAddRiskScenarioDialog(props: IAddRiskScenarioDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateRiskScenarioCategory } = useTranslation();

    const baseUrl = getRiskAssessmentSubjectUrl(props.riskAreaId, props.subject);
    const { isLoading, data } = useDotLegalQuery<Array<SelectableRiskScenario>>(`${baseUrl}/selectable-scenarios`);

    const [selectedScenarioIds, setSelectedScenarioIds] = useState<Array<string>>([]);
    const [selectedCategories, setSelectedCategories] = useState<Array<string>>([]);
    const [searchString, setSearchString] = useState("");

    const categoryOptions: Array<DotLegalSelectOption> = Object.values(RiskScenarioCategory)
        .filter((value): value is RiskScenarioCategory => typeof value === "number")
        .map((category) => ({ id: category.toString(), name: translateRiskScenarioCategory(category) }));

    const filteredScenarios = (data ?? []).filter((scenario) => selectedCategories.length === 0 || selectedCategories.includes(scenario.category.toString()));

    const rows: Array<SelectableScenarioRow> = filteredScenarios.map((scenario) => ({
        id: scenario.id,
        name: scenario.name,
        description: scenario.description,
        category: scenario.category,
        categoryLabel: translateRiskScenarioCategory(scenario.category),
        isSelected: selectedScenarioIds.includes(scenario.id),
    }));

    // The rows the table is actually showing — category filter (above) plus the table's search box.
    const visibleScenarioIds = rows.filter((row) => scenarioMatchesSearch(row, searchString)).map((row) => row.id);

    const toggleScenario = (id: string) => {
        setSelectedScenarioIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
    };

    // Select-all acts on the visible view only, preserving selections made under other filters.
    const onSelectAllChanged = () => {
        const allVisibleSelected = visibleScenarioIds.every((id) => selectedScenarioIds.includes(id));
        setSelectedScenarioIds((current) =>
            allVisibleSelected ? current.filter((id) => !visibleScenarioIds.includes(id)) : Array.from(new Set([...current, ...visibleScenarioIds]))
        );
    };

    const getHeaderCheckBoxState = (): "true" | "false" | "indeterminate" => {
        const noneSelected = visibleScenarioIds.every((id) => !selectedScenarioIds.includes(id));
        if (noneSelected) return "false";

        const allSelected = visibleScenarioIds.every((id) => selectedScenarioIds.includes(id));
        return allSelected ? "true" : "indeterminate";
    };

    const saveMutation = useDotLegalMutation(() => post(`${baseUrl}/scenarios`, selectedScenarioIds), {
        onSuccess: () => {
            snackbar.show(translateString("scenariosAdded"));
            props.onScenariosAdded();
            props.onCloseDialog();
        },
    });

    const onSaveClick = () => {
        saveMutation.mutateAsync();
    };

    return {
        isLoading,
        rows,
        hasScenarios: (data ?? []).length > 0,
        categoryOptions,
        selectedCategories,
        setSelectedCategories,
        toggleScenario,
        onSelectAllChanged,
        getHeaderCheckBoxState,
        setSearchString,
        onSaveClick,
        isSaving: saveMutation.isLoading,
        hasSelection: selectedScenarioIds.length > 0,
    };
}
